// frontend/src/pages/user/AppRecommendationsPage.js
import React, { useState, useRef, useEffect } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const AppRecommendationsPage = () => {
    const { userInfo } = useAuth();
    const [input, setInput] = useState('');
    
    const [messages, setMessages] = useState([
        { 
            role: 'bot', 
            text: `Merhaba ${userInfo?.name || ''}! 👋 Ben Akıllı Öneri Asistanı. İzlemek veya dinlemek istediğiniz bir şey var mı? Sistemdeki en uygun uygulamayı bulmanıza yardımcı olabilirim.` 
        }
    ]);
    const [loading, setLoading] = useState(false);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSend = async (e) => {
        e.preventDefault();
        if (!input.trim()) return;

        const userMessage = { role: 'user', text: input };
        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setLoading(true);

        try {
            const { data } = await api.post('/extras/ai', { 
                prompt: userMessage.text,
                history: messages
            });
            setMessages(prev => [...prev, { role: 'bot', text: data.reply }]);
        } catch (error) {
            setMessages(prev => [...prev, { role: 'bot', text: '⚠️ Asistan şu an yanıt veremiyor.' }]);
        } finally {
            setLoading(false);
        }
    };

    return (
        /* 
           DÜZELTME: Yükseklik Hesabı
           - UserLayout'un Header'ı, Footer'ı ve Padding'i toplamda yaklaşık 200px-240px yer kaplar.
           - h-[calc(100vh-200px)]: Mobilde alt footer ve menü payını düşerek tam oturttuk.
           - md:h-[calc(100vh-240px)]: Masaüstünde padding daha fazla olduğu için payı artırdık.
           - Bu sayede dış scrollbar asla çıkmayacak.
        */
        <div className="flex flex-col h-[calc(100vh-200px)] md:h-[calc(100vh-240px)] w-full overflow-hidden">
            
            {/* BAŞLIK ALANI (Eski boyutuna getirildi) */}
            <div className="flex items-center gap-3 mb-3 shrink-0">
                <h1 className="text-2xl md:text-3xl font-bold text-text-primary">Akıllı Öneriler</h1>
                <span className="px-2 py-0.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-[10px] md:text-xs font-bold rounded-full animate-pulse whitespace-nowrap">
                    AI Powered
                </span>
            </div>
            
            {/* MESAJ ALANI (flex-1 ile kalan alanı kaplar) */}
            <div className="flex-1 bg-surface rounded-xl p-3 md:p-4 overflow-y-auto custom-scrollbar space-y-4 mb-3 border border-border shadow-inner min-h-0">
                {messages.map((msg, idx) => (
                    <div key={idx} className={`flex items-start gap-2 md:gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                        
                        {/* Avatar */}
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg ${
                            msg.role === 'user' 
                            ? 'bg-primary text-white' 
                            : 'bg-gradient-to-br from-purple-600 to-blue-600 text-white'
                        }`}>
                            {msg.role === 'user' ? (
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" /></svg>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor"><path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" /></svg>
                            )}
                        </div>

                        {/* Balon */}
                        <div className={`max-w-[85%] md:max-w-[80%] p-3 rounded-2xl text-xs md:text-sm leading-relaxed shadow-md ${
                            msg.role === 'user' 
                            ? 'bg-primary text-white rounded-tr-none' 
                            : 'bg-background text-gray-200 rounded-tl-none border border-border'
                        }`}>
                            {msg.text.split('\n').map((line, i) => (
                                <p key={i} className="min-h-[1rem]">{line}</p>
                            ))}
                        </div>
                    </div>
                ))}
                
                {/* Typing Indicator */}
                {loading && (
                    <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center animate-pulse">
                            <span className="w-1 h-1 bg-white rounded-full"></span>
                        </div>
                        <div className="bg-background border border-border p-3 rounded-2xl rounded-tl-none flex gap-1 items-center h-8">
                            <span className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce"></span>
                            <span className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce delay-100"></span>
                            <span className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce delay-200"></span>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* INPUT ALANI (Sabit) */}
            <form onSubmit={handleSend} className="relative shrink-0">
                <input 
                    type="text" 
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Bir şeyler sorun..."
                    className="w-full bg-surface border border-border text-white rounded-xl pl-4 pr-12 md:pr-24 py-3 shadow-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-sm"
                />
                
                <button 
                    type="submit" 
                    disabled={loading || !input.trim()} 
                    className="absolute right-1.5 top-1.5 bottom-1.5 bg-primary hover:bg-blue-600 text-white px-3 md:px-5 rounded-lg font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                    <span className="hidden md:inline text-xs">Gönder</span>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                        <path d="M3.105 2.289a.75.75 0 00-.826.95l1.414 4.925A1.5 1.5 0 004.835 9.75h9.344a.75.75 0 010 1.5H4.835a1.5 1.5 0 00-1.142 1.586l1.414 4.925a.75.75 0 001.294.516l11.5-7a.75.75 0 000-1.286l-11.5-7z" />
                    </svg>
                </button>
            </form>
        </div>
    );
};

export default AppRecommendationsPage;